import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { authApi, type AuthUser } from '../api/auth';
import { clearAccessToken, getAccessToken, setAccessToken } from '../lib/auth-storage';

type AuthState = {
  user: AuthUser | null;
  loading: boolean;
  isAuthenticated: boolean;
  login: (email: string, password: string, rememberMe?: boolean) => Promise<void>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
};

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);
  const refreshTimer = useRef<number | null>(null);

  const scheduleRefresh = useCallback((expiresAt: string) => {
    if (refreshTimer.current) {
      window.clearTimeout(refreshTimer.current);
    }
    const ms = new Date(expiresAt).getTime() - Date.now() - 60_000;
    const run = () => {
      void authApi
        .refresh()
        .then((pair) => {
          setAccessToken(pair.access_token);
          scheduleRefresh(pair.expires_at);
        })
        .catch(() => {
          // Keep access token until /me next fails after cookie refresh window.
          // Only hard-logout when both access and refresh fail together.
        });
    };
    if (ms <= 0) {
      run();
      return;
    }
    refreshTimer.current = window.setTimeout(run, ms);
  }, []);

  const refreshUser = useCallback(async () => {
    let token = getAccessToken();
    if (!token) {
      // Cookie refresh may still restore the session without an access token in storage.
      try {
        const pair = await authApi.refresh();
        setAccessToken(pair.access_token);
        scheduleRefresh(pair.expires_at);
        token = pair.access_token;
      } catch {
        setUser(null);
        return;
      }
    }
    try {
      const me = await authApi.me();
      setUser(me);
    } catch {
      try {
        const pair = await authApi.refresh();
        setAccessToken(pair.access_token);
        scheduleRefresh(pair.expires_at);
        const me = await authApi.me();
        setUser(me);
      } catch {
        clearAccessToken();
        setUser(null);
      }
    }
  }, [scheduleRefresh]);

  useEffect(() => {
    void (async () => {
      try {
        await refreshUser();
      } finally {
        setLoading(false);
      }
    })();
    return () => {
      if (refreshTimer.current) {
        window.clearTimeout(refreshTimer.current);
      }
    };
  }, [refreshUser]);

  const login = useCallback(
    async (email: string, password: string, rememberMe = true) => {
      const data = await authApi.login(email, password, rememberMe);
      setUser(data.user);
      scheduleRefresh(data.tokens.expires_at);
    },
    [scheduleRefresh],
  );

  const logout = useCallback(async () => {
    await authApi.logout();
    setUser(null);
    if (refreshTimer.current) {
      window.clearTimeout(refreshTimer.current);
    }
  }, []);

  const value = useMemo(
    () => ({
      user,
      loading,
      isAuthenticated: !!user,
      login,
      logout,
      refreshUser,
    }),
    [user, loading, login, logout, refreshUser],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return ctx;
}
